import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Link2, MoreHorizontal, Save, Search } from 'lucide-react';
import { toast } from 'sonner';
import { toastUnlessRefused } from '../../../lib/errors';
import { useNavigate } from 'react-router-dom';
import type { FlowView, SavedViewCreateBody, SavedViewWire } from '@shared/flow/view';
import { viewInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import { Drawer } from '../../ui/Drawer';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { ResponsivePopover } from '../ResponsivePopover';
import { useFlowViews, useFlowViewMutations, useFlowPresetUsage, type FlowViewEntity, type FlowViewMutations } from './useViews';
import { useViewNameDialog } from './ViewNameDialog';
import { copyViewLink } from './copyViewLink';
import { OTHER_ENTITIES, PRESETS_BY_ENTITY, type FlowViewMachine } from './useViewStateMachine';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * BrowseAllModal — the "Browse all views…" surface (plan U3, superseding the
 * original U6 build), per the prototype's `openBrowse()`/`renderBrowse()`
 * (`docs/flow/custom-views-prototype.html`, ~line 287 for the markup and
 * ~line 961 for the behavior) — REVISED per Spencer's 2026-08-12 QA pass:
 *
 *  - Rebuilt on `components/ui/Drawer.tsx` (right-edge slide-over), not
 *    `ui/Modal` — `Modal.tsx` carries ZERO `dark:` classes (semantic tokens
 *    have no `.dark` overrides in this app, see `FlowTable.tsx`'s convention
 *    note), which was the entire root cause of the dark-mode bug this surface
 *    shipped with. `Drawer` itself now carries the `dark:` variants it was
 *    missing too — this modal is simply the first caller to need them.
 *  - Object filter is TABS (Contacts / Accounts / All), not a `<select>`,
 *    defaulting to the mount's own entity.
 *  - No preview pane, no row selection: every row exposes its THREE actions
 *    (Copy link / Open / Save as new view), so a rep never has to
 *    select-then-look-at-a-footer to act on a row.
 *
 * ── One menu per row, not three icon buttons (V5, QA-2) ──────────────────
 * Each row's three actions live behind ONE "⋯" trigger (`aria-label
 * "Actions for <name>"`) rather than three always-visible icon buttons — the
 * ROW itself gets less busy as more actions get added later. The menu is a
 * single shared `ResponsivePopover` (not one per row) repositioned via an
 * imperative anchor ref set to whichever row's trigger was clicked (the same
 * `nameDialogAnchorRef` trick this file already used for the naming dialog,
 * see below), so opening one row's menu can never leave a stale one floating
 * over a different row. ARIA follows `ContactActionMenus.tsx`'s dropdown
 * (`role="menu"` / `role="menuitem"`, ~line 260); each menuitem KEEPS the
 * exact per-row accessible name the old icon buttons carried ("Copy link to
 * <name>" / "Open <name>" / "Save <name> as new view") — existing tests
 * select on those names, and the accessibility tree is what `ab snapshot`
 * and screen readers read regardless of which control renders them.
 *
 * ── Same-entity Open (V1 → simplified 2026-08-12) ────────────────────────
 * "Open <name>" for a row belonging to THIS mount's own entity routes
 * through `machine.openView(id)`, which switches immediately — no
 * confirmation gate (see `useViewStateMachine.openView`'s doc for why one
 * used to exist and why it was removed). `onClose()` fires once `openView`
 * resolves `true` — i.e. the view actually changed (`false` only on an
 * unresolvable id). The FOREIGN-entity branch never goes through
 * `machine.openView` at all (see the "Cross-entity Open" note below) — that
 * navigate is a Command Center TAB switch, which would resolve the id
 * against the wrong entity's map.
 *
 * ── Three entities, one machine (widened for Signals, bead 2sx.7) ─────────
 * The caller only ever has ONE `FlowViewMachine` (the table it's mounted on).
 * This modal still has to show the OTHER TWO entities' views too (the
 * prototype's "All objects" default), so it fetches each one's saved views
 * directly via `useFlowViews` (the same cached-indefinitely query the
 * machine's own entity already uses) and reads each one's code presets from
 * the shared `PRESETS_BY_ENTITY` map — the SAME map `useViewStateMachine`
 * itself uses to build `FOREIGN_PRESET_IDS`/`OTHER_ENTITIES`, so this isn't a
 * new pattern, just widened from a 1:1 pair to a per-entity list.
 *
 * ── Cross-entity Open + Copy link (U3) ───────────────────────────────────
 * A same-entity row's Open/Copy link go straight through `machine.openView`/
 * `machine.copyLink` — those resolve ids against THIS table's own
 * presets/saved-views map, usage-ping included, for free. A FOREIGN-entity
 * row cannot go through the machine at all (it would resolve the id against
 * the wrong entity's map and silently "no longer exists"), so those two
 * actions instead build a `viewInCommandCenter(entity, id)` link
 * (`mini-apps/_shared/flow-links.ts`, the cross-app link registry) and
 * either `navigate()` there (Open) or copy an ABSOLUTE URL built from it
 * (Copy link — `window.location.origin` prefixed, since the registry only
 * ever returns origin-relative paths). The receiving mount's
 * `useViewStateMachine` resolves `?view=` the normal way — no special-casing
 * needed on the other end.
 *
 * ── Reuse, not reimplementation ──────────────────────────────────────────
 * Save-as-new calls the SAME `useFlowViewMutations().createView` the state
 * machine's own `saveAsNew()` calls — never a hand-rolled `apiFetch`/
 * validate — but supplies the ROW's definition rather than the table's live
 * `working` state, because the two can be completely different views.
 *
 * ── Preset usage (F12) ────────────────────────────────────────────────────
 * `GET /api/flow/views?entity=` returns `presetUsage` alongside `views` — a
 * map of THIS entity's preset slug → open count, read back from the same
 * `flow_view_usage` counter `POST /:id/open` already upserts for preset
 * slugs (Decision 4). `useFlowPresetUsage` reads it (same cached query as
 * `useFlowViews`, sliced differently — no second request); a preset absent
 * from the map has never been opened and sorts/renders as 0, exactly like a
 * saved view nobody has opened.
 */

export interface BrowseAllModalProps {
  open: boolean;
  onClose: () => void;
  machine: FlowViewMachine;
}

type EntityFilter = 'all' | FlowViewEntity;

const ENTITY_LABEL: Record<FlowViewEntity, string> = { contact: 'Contacts', account: 'Accounts', signal: 'Signals', play: 'Plays' };

/** Stable empty-map fallback for `useFlowPresetUsage` (F12) — see the
 *  comment where it's used for why an inline `{}` would be unsafe here. */
const EMPTY_PRESET_USAGE: Record<string, number> = {};

interface BrowseRow {
  id: string;
  name: string;
  entity: FlowViewEntity;
  kind: 'preset' | 'saved';
  /** The switcher's "preset" / "you" / teammate-name label. */
  ownerBadge: string;
  /** Always the real creator name (or "Terra" for a preset). */
  creatorDisplay: string;
  usageCount: number;
  filter: SavedViewCreateBody['filter'];
  groupLogic: SavedViewCreateBody['groupLogic'];
  sort: SavedViewCreateBody['sort'];
  groupBy: SavedViewCreateBody['groupBy'];
  columns: SavedViewCreateBody['columns'];
}

function presetsToRows(entity: FlowViewEntity, presets: FlowView[], usage: Record<string, number>): BrowseRow[] {
  return presets.map(p => ({
    id: p.id,
    name: p.name,
    entity,
    kind: 'preset' as const,
    ownerBadge: 'preset',
    creatorDisplay: 'Terra',
    // F12 — real counts from `flow_view_usage`, read back via
    // `useFlowPresetUsage`; a preset never opened is simply absent from the
    // map and falls back to 0 here, the same as an unopened saved view.
    usageCount: usage[p.id] ?? 0,
    filter: p.filter,
    groupLogic: p.groupLogic,
    sort: p.sort,
    groupBy: p.groupBy ?? null,
    columns: p.columns,
  }));
}

function savedToRows(entity: FlowViewEntity, saved: SavedViewWire[], myUserId: string | null): BrowseRow[] {
  return saved.map(v => ({
    id: v.id,
    name: v.name,
    entity,
    kind: 'saved' as const,
    ownerBadge: myUserId !== null && v.createdBy === myUserId ? 'you' : (v.creatorName ?? 'teammate'),
    creatorDisplay: v.creatorName ?? 'Unknown',
    usageCount: v.usageCount,
    filter: v.filter,
    groupLogic: v.groupLogic,
    sort: v.sort,
    groupBy: v.groupBy ?? null,
    columns: v.columns,
  }));
}

function suggestedCopyName(row: BrowseRow): string {
  if (row.kind === 'preset') return `${row.name} — from Terra`;
  return row.ownerBadge === 'you' ? `${row.name} (copy)` : `${row.name} — from ${row.creatorDisplay}`;
}

const TAB_CLASS =
  'rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors';
const TAB_ACTIVE_CLASS = 'bg-content-action text-white dark:bg-blueberry-500';
const TAB_INACTIVE_CLASS =
  'text-content-secondary hover:bg-bg-secondary dark:text-nightshade-400 dark:hover:bg-nightshade-800';

const ROW_CLASS =
  'flex items-center gap-2 rounded-md border border-transparent px-2.5 py-2 hover:bg-bg-secondary dark:hover:bg-nightshade-800';

const ROW_ACTION_BUTTON =
  'inline-flex items-center gap-1 rounded-md border border-secondary px-2 py-1 text-[11.5px] font-medium text-content-primary hover:bg-bg-secondary dark:border-nightshade-700 dark:text-nightshade-100 dark:hover:bg-nightshade-700';

/** V5 per-row action menu items (Copy link / Open view / Save as new view). */
const MENU_ITEM_CLASS =
  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-content-primary hover:bg-bg-secondary dark:text-nightshade-100 dark:hover:bg-nightshade-700';

export function BrowseAllModal({ open, onClose, machine }: BrowseAllModalProps) {
  const { userId } = useUserControlsContext();
  const myUserId = userId != null ? String(userId) : null;
  const navigate = useNavigate();

  // The modal shows only the FIRST TWO foreign entities: it calls one fixed
  // hook pair per entity unconditionally (Rules of Hooks), so it cannot walk
  // a dynamically-sized list. With four entities `OTHER_ENTITIES` has three
  // members; on a `play` machine (V1 Command Center Plays subtab — FlowTable
  // renders ViewSwitcher, which opens its own BrowseAllModal) that is
  // Contacts + Accounts, and Signals views are not browsable from here.
  // Accepted for legacy V1; tracked in the plan's "Deferred to Follow-Up Work"
  // (Record-driven OTHER_ENTITIES consumer). Do NOT add a third hook pair.
  const [entityA, entityB] = OTHER_ENTITIES[machine.entity] as [FlowViewEntity, FlowViewEntity, ...FlowViewEntity[]];
  const presetsA = PRESETS_BY_ENTITY[entityA];
  const presetsB = PRESETS_BY_ENTITY[entityB];

  const viewsQueryA = useFlowViews(entityA);
  const viewsA = useMemo(() => viewsQueryA.data ?? [], [viewsQueryA.data]);
  const viewsQueryB = useFlowViews(entityB);
  const viewsB = useMemo(() => viewsQueryB.data ?? [], [viewsQueryB.data]);

  // F12 — preset usage for all three entities shown in this modal. Same
  // cached query as `useFlowViews(entity)` (sliced via `select`), so this
  // costs no extra request. `?? EMPTY_PRESET_USAGE` (a module-level
  // constant, not `{}` inline) keeps the fallback reference STABLE across
  // renders — an inline `{}` is a fresh object every render, which would
  // make `allRows`'s `useMemo` below re-evaluate on every render.
  const ownPresetUsageQuery = useFlowPresetUsage(machine.entity);
  const ownPresetUsage = useMemo(() => ownPresetUsageQuery.data ?? EMPTY_PRESET_USAGE, [ownPresetUsageQuery.data]);
  const presetUsageQueryA = useFlowPresetUsage(entityA);
  const presetUsageA = useMemo(() => presetUsageQueryA.data ?? EMPTY_PRESET_USAGE, [presetUsageQueryA.data]);
  const presetUsageQueryB = useFlowPresetUsage(entityB);
  const presetUsageB = useMemo(() => presetUsageQueryB.data ?? EMPTY_PRESET_USAGE, [presetUsageQueryB.data]);

  // Fixed literals, always called in the same order (Rules of Hooks) — NOT a
  // dynamic `useFlowViewMutations(row.entity)` per render.
  const ownMutations = useFlowViewMutations(machine.entity);
  const mutationsA = useFlowViewMutations(entityA);
  const mutationsB = useFlowViewMutations(entityB);
  const mutationsForEntity = (entity: FlowViewEntity): FlowViewMutations => {
    if (entity === machine.entity) return ownMutations;
    return entity === entityA ? mutationsA : mutationsB;
  };

  const [query, setQuery] = useState('');
  // U3 — default the tab to the mount's OWN entity, not "All objects" (was
  // the prototype default; Spencer's 2026-08-12 feedback wants the table you
  // came from to lead).
  const [entityFilter, setEntityFilter] = useState<EntityFilter>(machine.entity);

  // U3 — actions are per-row now; the name dialog anchors to whichever row
  // button was actually clicked (Save as new / Copy link-while-dirty), set
  // synchronously in the click handler just before `requestName` opens it.
  // V5 reuses the SAME ref for the per-row action menu's own anchor: the
  // menu's trigger button is what gets clicked first, and it stays mounted
  // (the row survives the menu closing), so it's a stable anchor point for
  // the name dialog the chosen action opens.
  const nameDialogAnchorRef = useRef<HTMLElement | null>(null);
  const { requestName, dialog: nameDialog } = useViewNameDialog(nameDialogAnchorRef);

  // V5 — which row's "⋯" menu is open, if any. Holding the whole row (not
  // just an id) sidesteps any theoretical id collision between a CONTACT row
  // and an ACCOUNT row (presets are namespaced per entity, but nothing
  // enforces that for a hypothetical future id scheme).
  const [menuRow, setMenuRow] = useState<BrowseRow | null>(null);
  const menuAnchorRef = useRef<HTMLElement | null>(null);
  const openRowMenu = (row: BrowseRow, anchorEl: HTMLElement) => {
    menuAnchorRef.current = anchorEl;
    setMenuRow(row);
  };
  const closeRowMenu = () => setMenuRow(null);

  // F11 — cross-user shared data with no invalidation channel; refetch all
  // three entities' lists (views + preset usage ride the same query) the
  // moment this modal opens, rather than trusting the indefinite cache.
  useEffect(() => {
    if (!open) return;
    machine.refetchViews();
    void viewsQueryA.refetch();
    void viewsQueryB.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on OPEN only, not on every machine/viewsQueryA/viewsQueryB identity change.
  }, [open]);

  const allRows = useMemo(() => [
    ...presetsToRows(machine.entity, machine.presets, ownPresetUsage),
    ...savedToRows(machine.entity, [...machine.myViews, ...machine.teamViews], myUserId),
    ...presetsToRows(entityA, presetsA, presetUsageA),
    ...savedToRows(entityA, viewsA, myUserId),
    ...presetsToRows(entityB, presetsB, presetUsageB),
    ...savedToRows(entityB, viewsB, myUserId),
  ], [
    machine.entity, machine.presets, machine.myViews, machine.teamViews, myUserId,
    entityA, presetsA, viewsA, presetUsageA,
    entityB, presetsB, viewsB, presetUsageB,
    ownPresetUsage,
  ]);

  const q = query.trim().toLowerCase();
  const rows = useMemo(() => allRows
    .filter(r => entityFilter === 'all' || r.entity === entityFilter)
    .filter(r => !q || r.name.toLowerCase().includes(q) || r.creatorDisplay.toLowerCase().includes(q))
    .sort((a, b) => (b.usageCount - a.usageCount) || a.name.localeCompare(b.name)),
  [allRows, entityFilter, q]);

  const handleOpen = (row: BrowseRow) => {
    // Foreign entity — a Command Center TAB switch. `machine.openView` would
    // resolve the id against the WRONG entity's map here, so this is never
    // used for this branch.
    if (row.entity !== machine.entity) {
      navigate(viewInCommandCenter(row.entity, row.id));
      onClose();
      return;
    }
    // Same-entity Open switches immediately (no confirmation gate — see
    // `useViewStateMachine.openView`'s doc). `onClose` fires once the view
    // actually changed (`false` only on an unresolvable id).
    void machine.openView(row.id).then(applied => {
      if (applied) onClose();
    });
  };

  const handleCopyLink = (row: BrowseRow, anchorEl: HTMLElement) => {
    nameDialogAnchorRef.current = anchorEl;
    if (row.entity === machine.entity) {
      void machine.copyLink(row.id, suggested => requestName({
        title: 'Name this view',
        initialValue: suggested,
        confirmLabel: 'Save view',
      }));
      return;
    }
    // Foreign entity — never `machine.copyLink` (it would resolve the id
    // against the wrong entity's map). Build an ABSOLUTE URL from the
    // cross-app registry's origin-relative path so the copied link is
    // pasteable as-is, and make sure `tab=` (the OTHER entity's tab) rides
    // along — `machine.copyLink` only ever preserves the CURRENT `tab=`.
    void copyViewLink(`${window.location.origin}${viewInCommandCenter(row.entity, row.id)}`);
  };

  const handleSaveAsNew = async (row: BrowseRow, anchorEl: HTMLElement) => {
    nameDialogAnchorRef.current = anchorEl;
    const name = await requestName({
      title: 'Name this view',
      initialValue: suggestedCopyName(row),
      confirmLabel: 'Save view',
    });
    if (name === null) return;

    const body: SavedViewCreateBody = {
      entity: row.entity,
      name,
      filter: row.filter,
      groupLogic: row.groupLogic,
      sort: row.sort,
      groupBy: row.groupBy ?? null,
      columns: row.columns,
    };

    try {
      const mutations = mutationsForEntity(row.entity);
      const created = await mutations.createView(body);
      toast.success('Saved — copy its link to share it');
      if (row.entity === machine.entity) {
        // The create RESPONSE, applied directly — never `openView(created.id)`.
        // That resolves the id against the machine's `savedById`, which is
        // derived from the views query and, at this instant, still holds the
        // pre-create list: the new id is missing, so the machine took its
        // "that view no longer exists" branch and dropped the rep back onto
        // the default preset every time they saved from here (F6).
        machine.applyCreated(created);
      }
      onClose();
    } catch (err) {
      toastUnlessRefused(err, 'Failed to save view');
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      widthClassName="w-[480px] max-w-full"
      header={(
        <div className="flex w-full items-center justify-between px-5 py-4 border-b border-nightshade-200 dark:border-nightshade-700 flex-shrink-0">
          <h3 className="text-base font-semibold text-nightshade-900 dark:text-nightshade-100">Browse all views</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-1.5 rounded-md text-nightshade-400 hover:text-nightshade-600 hover:bg-nightshade-50 dark:text-nightshade-500 dark:hover:text-nightshade-100 dark:hover:bg-nightshade-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    >
      {/* Object filter — tabs, not a `<select>` (U3), default to the mount's
          own entity, "All" kept for the cross-entity browse case. */}
      <div role="tablist" aria-label="Filter by object" className="mb-2 flex items-center gap-1">
        {(['all', machine.entity, entityA, entityB] as EntityFilter[]).map(value => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={entityFilter === value}
            onClick={() => setEntityFilter(value)}
            className={`${TAB_CLASS} ${entityFilter === value ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS}`}
          >
            {value === 'all' ? 'All objects' : ENTITY_LABEL[value]}
          </button>
        ))}
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-tertiary dark:text-nightshade-500" />
        <input
          {...AUTOFILL_IGNORE_PROPS}
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label="Search views, creators…"
          placeholder="Search views, creators…"
          className="w-full rounded-md border border-secondary bg-white py-1 pl-7 pr-2 text-[12px] text-content-primary outline-none placeholder:text-content-placeholder focus:ring-1 focus:ring-content-action dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100"
        />
      </div>

      <div role="list" aria-label="Views" className="space-y-1">
        {rows.length === 0 ? (
          <p className="p-3 text-[12px] text-content-tertiary dark:text-nightshade-500">No views match.</p>
        ) : (
          rows.map(row => (
            <div key={row.id} role="listitem" data-testid="browse-row" className={ROW_CLASS}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[12.5px] font-medium text-content-primary dark:text-nightshade-100">{row.name}</span>
                  <span className="shrink-0 rounded bg-bg-secondary px-1 text-[10px] uppercase text-content-tertiary dark:bg-nightshade-700 dark:text-nightshade-400">
                    {ENTITY_LABEL[row.entity]}
                  </span>
                </div>
                <div className="text-[11px] text-content-tertiary dark:text-nightshade-500">
                  {row.kind === 'preset' ? 'Terra' : row.ownerBadge}
                  {` · used ${row.usageCount} time${row.usageCount === 1 ? '' : 's'}`}
                </div>
              </div>
              <div className="flex shrink-0 items-center">
                {/* V5 — one menu per row (Copy link / Open view / Save as new
                    view), replacing the three always-visible icon buttons.
                    `aria-label` names the ROW, not just "more" — the same
                    per-row-unique convention the menu items themselves keep
                    (R3, see the module doc). */}
                <button
                  type="button"
                  aria-label={`Actions for ${row.name}`}
                  title={`Actions for ${row.name}`}
                  aria-haspopup="menu"
                  aria-expanded={menuRow?.id === row.id}
                  onClick={e => openRowMenu(row, e.currentTarget)}
                  className={ROW_ACTION_BUTTON}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* One shared menu, repositioned per row via `menuAnchorRef` (V5) — see
          the module doc's "One menu per row" note for why this isn't one
          `ResponsivePopover` per row. ARIA mirrors `ContactActionMenus.tsx`'s
          dropdown (`role="menu"`/`role="menuitem"`, ~line 260). */}
      <ResponsivePopover
        open={menuRow !== null}
        onClose={closeRowMenu}
        anchorRef={menuAnchorRef}
        ariaLabel={menuRow ? `Actions for ${menuRow.name}` : 'Row actions'}
        className="w-52 overflow-hidden"
      >
        {menuRow && (
          <div role="menu" aria-label={`Actions for ${menuRow.name}`} className="py-1">
            <button
              type="button"
              role="menuitem"
              aria-label={`Copy link to ${menuRow.name}`}
              title={`Copy link to ${menuRow.name}`}
              onClick={() => {
                const row = menuRow;
                const anchor = menuAnchorRef.current;
                closeRowMenu();
                if (anchor) handleCopyLink(row, anchor);
              }}
              className={MENU_ITEM_CLASS}
            >
              <Link2 className="h-3.5 w-3.5 shrink-0" />
              Copy link
            </button>
            <button
              type="button"
              role="menuitem"
              aria-label={`Open ${menuRow.name}`}
              title={`Open ${menuRow.name}`}
              onClick={() => {
                const row = menuRow;
                const anchor = menuAnchorRef.current;
                closeRowMenu();
                if (anchor) handleOpen(row);
              }}
              className={MENU_ITEM_CLASS}
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              Open view
            </button>
            <button
              type="button"
              role="menuitem"
              aria-label={`Save ${menuRow.name} as new view`}
              title={`Save ${menuRow.name} as new view`}
              onClick={() => {
                const row = menuRow;
                const anchor = menuAnchorRef.current;
                closeRowMenu();
                if (anchor) void handleSaveAsNew(row, anchor);
              }}
              className={MENU_ITEM_CLASS}
            >
              <Save className="h-3.5 w-3.5 shrink-0" />
              Save as new view
            </button>
          </div>
        )}
      </ResponsivePopover>

      {nameDialog}
    </Drawer>
  );
}
